import styles from '../Confirmation.module.scss'
import { ConfirmationProps } from '../types'
import { useStoreSelector } from 'hooks/store'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'
import { useGetCheckIsPlentyInvestorQuery } from 'store/api/bolt-api'
import ConfirmationWrapper from '../components/ConfirmationWrapper'
import { Card, Heading, LoadingSkeleton, StepList } from '@boltenergy-be/design-system'
import parse from 'html-react-parser'
import { PLENTY_URL } from 'constants/constants'
import Link from 'components/link/Link'
import { checkEnforcePlenty } from 'features/simulation/utils'
import { useMemo } from 'react'
import { routes } from 'utils/routes'
import { InputGroupKeys } from 'store/customer/enums'

const PlentyPropositionsView = ({ producer }: ConfirmationProps) => {
  // Redux state
  const { inputs } = useStoreSelector((store) => store.customer)
  const { availableProducts } = useStoreSelector((store) => store.marketing)
  const { opportunityId } = useStoreSelector((store) => store.app)

  // Redux query
  const { data: plentyInvestorCheckData, isLoading: isFetchingPlentyInvestorCheck } = useGetCheckIsPlentyInvestorQuery(opportunityId, {
    skip: !opportunityId
  })

  // Next router
  const { locale } = useRouter()

  // i18n
  const { t } = useTranslation('registration')

  // Constants
  const enforcePlenty = checkEnforcePlenty({ region: inputs[InputGroupKeys.SIMULATION].region, availableProducts })
  const context = useMemo(() => {
    if (enforcePlenty) {
      if (plentyInvestorCheckData?.exists) {
        return plentyInvestorCheckData?.isInvestor ? 'existingInvestor_enforced' : 'investorWaitingForPayment_enforced'
      } else {
        return 'newInvestor_enforced'
      }
    } else {
      return plentyInvestorCheckData?.isInvestor ? 'existingInvestor_default' : 'newInvestor_default'
    }
  }, [enforcePlenty, plentyInvestorCheckData])

  return (
    <ConfirmationWrapper limitedWidth>
      <header>
        {isFetchingPlentyInvestorCheck ? (
          <LoadingSkeleton.Title />
        ) : (
          <Heading as="h1" variant="h2">
            {t('confirmation.plentyPropositions.title', {
              context: enforcePlenty && !plentyInvestorCheckData?.isInvestor ? 'soClose' : 'existing'
            })}
          </Heading>
        )}
        <p>{t('confirmation.plentyPropositions.description')}</p>
      </header>

      <Card>
        {isFetchingPlentyInvestorCheck ? (
          <LoadingSkeleton className="gap-600">
            <LoadingSkeleton.Rectangle width="100%" height={58} />
            <LoadingSkeleton.Rectangle width="100%" height={58} />
          </LoadingSkeleton>
        ) : (
          <StepList size="large" className={styles['step-list']}>
            {(
              t('confirmation.plentyPropositions.list', {
                returnObjects: true,
                producer: producer.name[locale],
                context
              }) as {
                title: string
                description: string
                action?: string
              }[]
            ).map((item, index) => (
              <li key={`confirmation.plentyPropositions-${index}`}>
                <div>
                  <strong>{item.title}</strong>
                  <span>{parse(item.description)}</span>
                </div>

                {item.action && !plentyInvestorCheckData?.isInvestor && (
                  <Link
                    as="a"
                    representation="button"
                    href={
                      enforcePlenty
                        ? `${PLENTY_URL}/${locale}/invest?simulation_so=Magic%20Link%20Bolt%20Page&boltOpportunityId=${opportunityId}`
                        : `${PLENTY_URL}/${locale}/invest?simulation_so=Bolt%20website`
                    }
                    target="_blank"
                    className={styles['plenty-invest-link']}
                  >
                    {item.action}
                  </Link>
                )}
              </li>
            ))}
          </StepList>
        )}
      </Card>

      {enforcePlenty && !isFetchingPlentyInvestorCheck && plentyInvestorCheckData?.exists && (
        <>
          <small className={styles.close}>{t('confirmation.closeWindow')}</small>

          <div className={styles['plenty-actions']}>
            <Link representation="button" href={routes(locale).home}>
              {t('confirmation.actions.toBolt')}
            </Link>

            <Link representation="button" as="a" href={`${PLENTY_URL}/${locale}/invest?simulation_so=Magic%20Link%20Bolt%20Page`}>
              {t('confirmation.actions.toPlenty')}
            </Link>
          </div>
        </>
      )}
    </ConfirmationWrapper>
  )
}

export default PlentyPropositionsView
